import { DateTime } from 'luxon';
import * as yup from 'yup';
import { SelectOption } from '../../data/selectInput.type';
import { Microcopy, MicrocopyResource } from '../../utilities/contentful';
import { PrimaryInsured } from './QuoteRequestData';

export class QuoteRequestValidation {
  individualSchemaShape;  
  individualSchema;
  individualSchemaArray;
  primaryInsuredSchema;
  quoteValidationSchema;
  
  micro = new Microcopy;

  constructor(microcopy:MicrocopyResource[]) {
    const classScope = this;

    this.individualSchemaShape = {
      age: yup
        .number()
        .required(this.micro.getFromKey('validation.age-required', microcopy))
        .min(1, this.micro.getFromKey('validation.minimum-age', microcopy))
        .integer(this.micro.getFromKey('validation.whole-numbers', microcopy)),

      genderObject: yup
        .object().shape({
          value: yup.string(),
          label: yup.string(),
        })
        .nullable()
        .required(this.micro.getFromKey('validation.gender-required', microcopy)),

      tripCost: yup
        .string()
        .test({
          name: "tripProtection",
          test: function (value) {
            const { parent, from } = this as any;
            const rootParent = from[from.length - 1].value;

            if (rootParent.tripProtection === 'no') {
              return true;

            } else if (rootParent.tripProtection === 'yes' && rootParent.useOverallTripCost === 'yes') {
              // trip protection is requested, but using one trip cost value instead of individual values
              return true;

            } else {
              if (value === undefined) {
                return this.createError({
                  'message': classScope.micro.getFromKey('validation.trip-cost-required', microcopy)
                });
              } else if (Number(value) < 500) {
                return this.createError({
                  'message': classScope.micro.getFromKey('validation.img-min-trip-cost', microcopy)
                });
              } else {
                return true;
              }
            }
          }
        }),

      citizenshipObject: yup
        .object().shape({
          value: yup.string(),
          label: yup.string(),
        })
        .nullable()
        .required(this.micro.getFromKey('validation.citizenship-required', microcopy)),

      residencyCountryObject: yup
        .object().shape({
          value: yup.string(),
          label: yup.string(),
        })
        .nullable()
        .required(this.micro.getFromKey('validation.residence-required', microcopy))
        .test({
          name: "countryAvailable",
          test: function (selected: SelectOption) {
            if (selected?.value !== 'USA') {
              return this.createError({
                'message': classScope.micro.getFromKey('validation.product-not-available', microcopy)
              });
            } else {
              return true;
            }
          }
        }),

      residencyStateObject: yup
        .object().shape({
          value: yup.string(),
          label: yup.string(),
        })
        .nullable()
        .when("residencyCountryObject", {
          is: (country: {value: string, label:string}) => {return country?.value === 'USA'},
          then: yup
            .object().shape({
              value: yup.string(),
              label: yup.string(),
            })
            .nullable()
            .required(this.micro.getFromKey('validation.state-required', microcopy))
        })
    }

    this.individualSchema = yup.object().shape(this.individualSchemaShape);
    this.individualSchemaArray = yup.array().of(this.individualSchema);

    this.primaryInsuredSchema = yup.object().shape({
      ...this.individualSchemaShape,
      hasSpouse: yup.boolean(),
      hasChildren: yup.boolean(),
      children: yup.array().when("hasChildren", {
        is: true,
        then: this.individualSchemaArray
      }),
      spouse: yup.object().when("hasSpouse", {
        is: true,
        then: this.individualSchema
      })
    })

    // An untouched picker holds an empty string, which yup reads as a date it
    // failed to parse, so an empty field said "Invalid date." instead of asking
    // for one. Empty now counts as missing.
    const emptyAsMissing = (value: unknown, original: unknown) => (original === '' ? null : value);

    this.quoteValidationSchema = yup.object({
      startDate: yup
        .date()
        .transform(emptyAsMissing)
        .nullable()
        .typeError(this.micro.getFromKey('validation.invalid-date', microcopy))
        .required(this.micro.getFromKey('validation.date-required', microcopy))
        .test('departs-after-today', this.micro.getFromKey('validation.departure-after-tomorrow', microcopy), (value) => !value || value >= new Date(new Date().setHours(24, 0, 0, 0))),

      endDate: yup
        .date()
        .transform(emptyAsMissing)
        .nullable()
        .typeError(this.micro.getFromKey('validation.invalid-date', microcopy))
        .required(this.micro.getFromKey('validation.date-required', microcopy))
        .when("startDate", (startDate, schema) => {
          const startDateLuxon = DateTime.fromJSDate(startDate) as DateTime;

          if (startDateLuxon.isValid) {
            const dayAfter = startDateLuxon.plus({day: 1})
            return schema.min(dayAfter.toJSDate(), this.micro.getFromKey('validation.return-date-after-departure', microcopy));
          }

          return schema;
        }).when("startDate", (startDate, schema) => {
          const startDateLuxon = DateTime.fromJSDate(startDate) as DateTime;

          if (startDateLuxon.isValid) {
            const oneEightyAfter = startDateLuxon.plus({day: 180});
            return schema.max(oneEightyAfter.toJSDate(), this.micro.getFromKey('validation.max-trip-length', microcopy));
          }

          return schema;
        }),

      usaTrip: yup
        .string()
        .nullable()
        .required(this.micro.getFromKey('validation.usa-trip', microcopy)),

      countryObject: yup
        .object().shape({
          value: yup.string(),
          label: yup.string(),
        })
        .nullable()
        .when("usaTrip", {
          is: "no",
          then: yup
            .object().shape({
              value: yup.string(),
              label: yup.string(),
            })
            .nullable()
            .required(this.micro.getFromKey('validation.select-primary-country', microcopy))
            .test({
              name: "defaultSelected",
              test: function (value:SelectOption | null) {
                if (!value || (value && value.value === 'none')) {
                  return this.createError({
                    'message': 'Please select a country'
                  });
                } else {
                  return true;
                }
              }
            })
        }),

      primaryInsured: yup.array().of(this.primaryInsuredSchema).test({
        name: 'minPrimaryInsured',
        test: function(arr:any) {
          if (arr.length == 0) {
            return this.createError({
              'message': 'At least one primary insured is required'
            });
          } else {
            return true;
          }
        },
      }),

      tripProtection: yup
        .string()
        .nullable()
        .required('Please choose an option for trip cancellation/interruption coverage.'),

      useOverallTripCost: yup
        .string()
        .nullable()
        .required('Please choose an option for determining overall trip cost.'),

      overallTripCost: yup
        .string()
        .test({
          name: "overallTripCost",
          test: function (value) {
            // get total number of people on the request            
            let personCount = 0;
            if (this.parent.primaryInsured && this.parent.primaryInsured.length > 0) {
              for (let insured of this.parent.primaryInsured as PrimaryInsured[]) {
                personCount++;

                if (insured.hasSpouse) personCount++;

                if (insured.hasChildren) {
                  personCount += insured.children.length;
                }
              }

              const minOverallAmount = 500 * personCount;

              if (this.parent.tripProtection === 'no') {
                return true;

              } else if (this.parent.tripProtection === 'yes' && this.parent.useOverallTripCost === 'yes') {
                if (value === undefined || Number(value) === NaN) {
                  return this.createError({
                    'message': classScope.micro.getFromKey('validation.cost-required', microcopy)
                  });
                } else if (Number(value) < minOverallAmount) {
                  return this.createError({
                    'message': classScope.micro
                      .getFromKey('validation.img-min-trip-cost-long', microcopy)
                      .replace('[PERSONCOUNT]', personCount.toString())
                      .replace('[MINAMOUNT]', `$${minOverallAmount.toLocaleString()}`)
                  });
                } else {
                  return true;
                }
              } else {
                return true;
              }
            } else {
              return true;
            }
          }
        }),

      initialPaymentDate: yup
        .date()
        .typeError(this.micro.getFromKey('validation.invalid-date', microcopy))
        .when("tripProtection", {
          is: "yes",
          then: yup
            .date()
            .required(this.micro.getFromKey('validation.first-payment-date-required', microcopy))
            .typeError(this.micro.getFromKey('validation.invalid-date', microcopy))
            .max(new Date(new Date().setHours(0, 0, 0, 0)), this.micro.getFromKey('validation.first-payment-date-past', microcopy))
            // .min(new Date(DateTime.now().minus({ days: 20 }).toJSDate().setHours(0, 0, 0, 0)), 'Initial payment date must be within the last 20 days')
        })
    });
  }
}